import { defineStore } from 'pinia';
import { handleError } from '@/utils/error-handler';

export const useAppStore = defineStore('app', {
    state: () => ({
        sidebarOpen: true,
        wallet: {},
        walletId: 0,
        issuingInstitution: '',
        version: '',
        phone: null,
        previousRoute: null,
        showAffiliateModal: false,
        showNextCardMethodModal: false,
        breadcrumbs: {},
        userSettings: {
            features: {},
            uuid: null,
        },
        invitationBannerVisible: false,
        paymentBannerVisible: false,
        dismissedPopups: {},
        hasShownPopupThisSession: false,
    }),
    getters: {
        showCspPopup(state) {
            return state.userSettings.show_csp_popup;
        },
        /**
         * Returns the name of the highest-priority popup that should be shown,
         * excluding any already dismissed in this session (via cookie or user action).
         */
        activePopup(state) {
            if (state.hasShownPopupThisSession) {
                return null;
            }
            if (
                state.userSettings.show_travel_plus_popup &&
                !state.dismissedPopups['travel-plus']
            ) {
                return 'travel-plus';
            }
            return null;
        },
        hashedEmail(state) {
            return this.hash(state.userSettings.email);
        },
        hasBannerVisible(state) {
            return state.invitationBannerVisible || state.paymentBannerVisible;
        },
    },
    actions: {
        setPreviousRoute(route) {
            this.previousRoute = route;
        },
        setSidebarOpen(data) {
            this.sidebarOpen = data;
        },
        setWallet(data) {
            this.wallet = data;
        },
        setWalletId(data) {
            this.walletId = data;
        },
        setAppWalletId(data) {
            this.walletId = data;
        },
        setInstitution(data) {
            ApiService.apiCall(
                '/admin/institution-by-issuing-bank-id/' + data,
                'get'
            )
                .then((response) => {
                    this.issuingInstitution = response.data.institution_code;
                })
                .catch((response) => {
                    handleError(response);
                });
        },
        setVersion(data) {
            this.version = data;
        },
        setPhone(data) {
            this.phone = data;
        },
        setShowAffiliateModal(set) {
            this.showAffiliateModal = set;
        },
        setShowNextCardMethodModal(set) {
            this.showNextCardMethodModal = set;
        },
        setShowLastFour(val) {
            this.userSettings.show_last_four = val;
            ApiService.apiCall('/user/setting', 'put', {
                setting: 'show_last_four',
                value: val,
            })
                .then((response) => {})
                .catch((response) => {});
        },
        setBreadcrumbs(key, val) {
            this.breadcrumbs[key] = val;
        },
        getUserSettings() {
            return this.userSettings;
        },
        setUserSettings(settings) {
            this.userSettings = settings;
        },
        dismissPopup(name) {
            this.dismissedPopups[name] = true;
        },
        markSessionPopupShown() {
            this.hasShownPopupThisSession = true;
        },
        setInvitationBannerVisible(val) {
            this.invitationBannerVisible = val;
        },
        setPaymentBannerVisible(val) {
            this.paymentBannerVisible = val;
        },
        initializeBannerVisibilityFromProps(pageProps) {
            // Pre-calculate banner visibility from Inertia props to avoid layout shift
            // This should be called during Main.vue setup before child banner components mount

            // Initialize invitation banner visibility
            try {
                // Check invitation banner conditions
                const userInvitations = pageProps?.user_invitations;
                const pendingInvites = Array.isArray(userInvitations)
                    ? userInvitations.filter((invite) => invite.status === 'N')
                    : [];
                const hasPendingInvitations = pendingInvites.length > 0;

                // Check email verification banner (part of invitation banner component)
                const userData = pageProps?.userData || {};
                const emailVerified = !!userData?.email_verified;
                const emailDismissed =
                    typeof sessionStorage !== 'undefined' &&
                    sessionStorage.getItem('dismiss_email_banner') === '1';
                const showEmailBanner = !emailVerified && !emailDismissed;

                // Set invitation banner visible if either condition is true
                this.invitationBannerVisible =
                    hasPendingInvitations || showEmailBanner;
            } catch (error) {
                console.error(
                    'Error initializing invitation banner visibility:',
                    error
                );
                this.invitationBannerVisible = false;
            }

            // Initialize payment banner visibility (independent from invitation banner)
            try {
                const paymentIssue =
                    pageProps?.subscription_payment_issue || null;
                const paymentDismissed =
                    typeof sessionStorage !== 'undefined' &&
                    sessionStorage.getItem('dismiss_payment_issue_banner') ===
                        '1';
                this.paymentBannerVisible = !!paymentIssue && !paymentDismissed;
            } catch (error) {
                console.error(
                    'Error initializing payment banner visibility:',
                    error
                );
                this.paymentBannerVisible = false;
            }
        },
        hash(string) {
            const utf8 = new TextEncoder().encode(string);
            return crypto.subtle.digest('SHA-256', utf8).then((hashBuffer) => {
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                const hashHex = hashArray
                    .map((bytes) => bytes.toString(16).padStart(2, '0'))
                    .join('');
                return hashHex;
            });
        },
    },
});
